/**
 * Regular expression pattern for matching a hexadecimal color code.
 */
const HEX_COLOR_MATCHER =
  /^#(?<r>\p{AHex}{2})(?<g>\p{AHex}{2})(?<b>\p{AHex}{2})$/u;

/**
 * Converts a color string to its corresponding hexadecimal representation. It works in almost
 * every browser, even in really old ones.
 * It converts any CSS color string to a hexadecimal representation, from any format (rgb, rgba, hsl, hsla, hex, named CSS color...) to a hexadecimal string.
 * If the conversion fails, the fallback color will be returned.
 * Modified from https://stackoverflow.com/a/24366628
 *
 * @param {string} colorStr - The color string to convert.
 * @param {string} [fallbackColor="#000000"] - The fallback color to use if the conversion fails.
 * @returns {string} The hexadecimal representation of the color string, or the fallback color.
 */
export function getHexColorFromCSSColor(colorStr, fallbackColor = "#000000") {
  const a = document.createElement("div");
  a.style.color = colorStr;

  const colors = window
    .getComputedStyle(document.body.appendChild(a))
    .color.match(/\d+/g)
    .map(function (a) {
      return parseInt(a, 10);
    });

  document.body.removeChild(a);

  return colors.length >= 3
    ? "#" +
        ((1 << 24) + (colors[0] << 16) + (colors[1] << 8) + colors[2])
          .toString(16)
          .substr(1)
    : fallbackColor || "#000000";
}

/**
 * Converts a hexadecimal color code to an array of RGB values.
 *
 * @param {string} hex - The hexadecimal color code (6 digits).
 * @returns {number[]} An array of RGB values.
 * @throws {Error} If the input is not a valid 6-digit hex color.
 */
function hexToSrgb(hex) {
  const match = hex.match(HEX_COLOR_MATCHER);
  if (!match) throw new Error("Not a valid 6-digit hex color");
  const { r, g, b } = match.groups;

  return [r, g, b].map(x => parseInt(x, 16) / 255);
}

/**
 * Calculates the relative luminance of a given sRGB color.
 *
 * @param {number[]} srgb - An array representing the sRGB color values [R, G, B].
 * @returns {number} The relative luminance value.
 */
function relativeLuminance(srgb) {
  const [R, G, B] = srgb.map(i =>
    i <= 0.04045 ? i / 12.92 : ((i + 0.055) / 1.055) ** 2.4
  );
  return 0.2126 * R + 0.7152 * G + 0.0722 * B;
}

/**
 * Calculates the text color based on the background color.
 *
 * In the future, instead of this, we could just use CSS: https://developer.mozilla.org/en-US/docs/Web/CSS/color_value/color-contrast#browser_compatibility
 *
 * @param {string} cssColor - The background color in any valid CSS color format.
 * @param {string} [lightColor="#000000"] - The default light color.
 * @param {string} [darkColor="#ffffff"] - The default dark color.
 * @returns {string} - The calculated text color.
 */
export function getTextColorBasedOnBackground(
  cssColor,
  lightColor = "#000000",
  darkColor = "#ffffff"
) {
  const hexColorFromCSSColor = getHexColorFromCSSColor(cssColor);

  return relativeLuminance(hexToSrgb(hexColorFromCSSColor)) > 0.179
    ? (lightColor ?? "#000000")
    : (darkColor ?? "#ffffff");
}

/**
 * Returns the extracted [r, g, b, a] values from a string
 * like "rgba(0, 5, 255, 0.8)". If no alpha is specified, return
 * undefined for it.
 *
 * @param {string} str Color in string.
 *
 * @return {array} Array with rgba values.
 */
export function rgbaFromString(str) {
  const m = str.match(
    /^rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*(\d+(?:\.\d+)?)\s*)?\)$/i
  );
  if (m) {
    return [
      parseInt(m[1]),
      parseInt(m[2]),
      parseInt(m[3]),
      m[4] === undefined ? 255 : parseFloat(m[4])
    ];
  } else {
    throw new Error(
      "Color " + str + " did not match pattern rgb[a](r, g, b[, a])."
    );
  }
}

/**
 * Return the extracted a string "rgba(r, g, b, a)",
 * from values in input array.
 *
 * @param {array} rgba Color in rgba array.
 *
 * @return {string} String with rgba values.
 */
export function stringFromRgba(rgba) {
  if (rgba && rgba.length == 4) {
    return (
      "rgba(" + rgba[0] + "," + rgba[1] + "," + rgba[2] + "," + rgba[3] + ")"
    );
  } else {
    throw new Error(
      "Color " + rgba + " did not match pattern rgba(r, g, b, a)."
    );
  }
}

/**
 * Return a string "rgba(r, g, b, a)" containing RGB values
 * from the input string multiplied by a modulating factor
 * typically smaller than 1.0.
 *
 * @param {string} rgba Color in string.
 * @param {number} factor Modulating factor.
 *
 * @return {string} String with rgba values.
 *
 */
export function modulateRgbaString(rgba, factor) {
  let colorComponents = rgbaFromString(rgba);
  colorComponents[0] *= factor;
  colorComponents[1] *= factor;
  colorComponents[2] *= factor;
  return stringFromRgba(colorComponents);
}

/**
 * Given an HEX array it returns a rgba array with [R,G,B,A] values
 * between 0 and 255.
 *
 * @param {array} hex
 *
 * @returns {array} RGBA Color array.
 */
export function hexToRgba(hex) {
  var c;
  if (hex.includes("rgb")) {
    c = hex.substr(5).split(")")[0].split(",");
    c = c.map((item, index) => {
      if (index === 3) {
        item = parseInt(parseFloat(item) * 255);
      } else {
        item = parseInt(item);
      }
      return item;
    });
    return c;
  }
  if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
    c = hex.substring(1).split("");
    if (c.length === 3) {
      c = [c[0], c[0], c[1], c[1], c[2], c[2]];
    }
    c = "0x" + c.join("");
    return [(c >> 16) & 255, (c >> 8) & 255, c & 255, 255];
  }
  throw new Error("Bad Hex");
}

/**
 * Converts RGBA to HEX.
 * @param {string} rgba Color in rgba.
 *
 * @returns {string} Hex color.
 */
export function componentToHex(rgba) {
  let hex = rgba.toString(16);
  return hex.length === 1 ? "0" + hex : hex;
}

/**
 * Hexadecimal to rgb color.
 *
 * @param {string} hex HEX Color
 *
 * @returns {object} RGB Color.
 */
export function hexToRgb(hex) {
  let result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      }
    : null;
}

/**
 * Calculate complementary color.
 *
 * @param {number} r
 * @param {number} g
 * @param {number} b
 *
 * @returns {string}
 */
export function rgbComplimentary(r, g, b) {
  let hex = "#" + componentToHex(r) + componentToHex(g) + componentToHex(b);
  let rgb =
    "rgb(" +
    (hex = hex.replace("#", ""))
      .match(new RegExp("(.{" + hex.length / 3 + "})", "g"))
      .map(function (l) {
        return parseInt(hex.length % 2 ? l + l : l, 16);
      })
      .join(",") +
    ")";

  // Get array of RGB values
  rgb = rgb.replace(/[^\d,]/g, "").split(",");

  let newR = rgb[0] / 255.0,
    newG = rgb[1] / 255.0,
    newB = rgb[2] / 255.0;

  let max = Math.max(newR, newG, newB);
  let min = Math.min(newR, newG, newB);
  let h,
    s,
    l = (max + min) / 2.0;

  if (max === min) {
    h = s = 0; //achromatic
  } else {
    let d = max - min;
    s = l > 0.5 ? d / (2.0 - max - min) : d / (max + min);

    if (max === newR && newG >= newB) {
      h = (1.0472 * (newG - newB)) / d;
    } else if (max === newR && newG < newB) {
      h = (1.0472 * (newG - newB)) / d + 6.2832;
    } else if (max === newG) {
      h = (1.0472 * (newB - newR)) / d + 2.0944;
    } else if (max === newB) {
      h = (1.0472 * (newR - newG)) / d + 4.1888;
    }
  }

  h = (h / 6.2832) * 360.0 + 0;

  // Shift hue to opposite side of wheel and convert to [0-1] value
  h += 180;
  if (h > 360) {
    h -= 360;
  }
  h /= 360;

  // Convert h s and l values into newR newG and newB values
  // Adapted from answer by Mohsen http://stackoverflow.com/a/9493060/4939630
  if (s === 0) {
    newR = newG = newB = l; // achromatic
  } else {
    var hue2rgb = function hue2rgb(p, q, t) {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    let q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    let p = 2 * l - q;

    newR = hue2rgb(p, q, h + 1 / 3);
    newG = hue2rgb(p, q, h);
    newB = hue2rgb(p, q, h - 1 / 3);
  }

  newR = Math.round(newR * 255);
  newG = Math.round(newG * 255);
  newB = Math.round(newB * 255);

  // Convert newR newB and newG values to hex
  rgb = newB | (newG << 8) | (newR << 16);
  return hexToRgb("#" + (0x1000000 | rgb).toString(16).substring(1));
}

/**
 * Checks if a color is empty (transparent or all zeros).
 * A color is considered empty if its alpha is 0 or if all RGBA values are 0.
 * @param {number[]} rgba - Array with [R, G, B] values (0-255) and A value (0-1).
 * @returns {boolean} True if the color is empty, false otherwise.
 */
export function isColorEmpty(rgba) {
  if (!rgba || rgba.length < 4) {
    return true;
  }
  // Check if alpha is 0 or if all RGB values are 0
  // RGB are 0-255, alpha is 0-1
  return (
    rgba[3] === 0 ||
    (rgba[0] === 0 && rgba[1] === 0 && rgba[2] === 0 && rgba[3] === 0)
  );
}

/**
 * Strips leading and trailing transparent colors from a color array, preserving transparent
 * colors in the middle (like trim() for strings).
 *
 * @param {Array<{hex: string, originalIndex: number, isEmpty: boolean}>} colorArray - Array of color objects with isEmpty flag.
 * @returns {Array<{hex: string, originalIndex: number}>} Array with leading/trailing transparent colors removed.
 */
function _paletteToHexArrayStripTransparent(colorArray) {
  if (!Array.isArray(colorArray) || colorArray.length === 0) {
    return [];
  }

  // Find first and last non-empty colors
  const firstNonEmpty = colorArray.findIndex(item => !item.isEmpty);
  const lastNonEmpty = colorArray.findLastIndex(item => !item.isEmpty);

  // If all colors are empty, return empty array
  if (firstNonEmpty === -1 || lastNonEmpty === -1) {
    return [];
  }

  // Return slice with leading/trailing transparents removed, but middle ones preserved
  return colorArray.slice(firstNonEmpty, lastNonEmpty + 1).map(item => ({
    hex: item.hex,
    originalIndex: item.originalIndex
  }));
}

/**
 * Filters out all transparent colors from a color array.
 *
 * @param {Array<{hex: string, originalIndex: number, isEmpty: boolean}>} colorArray - Array of color objects with isEmpty flag.
 * @returns {Array<{hex: string, originalIndex: number}>} Array with all transparent colors removed.
 */
function _paletteToHexArrayFilterTransparent(colorArray) {
  if (!Array.isArray(colorArray) || colorArray.length === 0) {
    return [];
  }

  // Filter out all transparent colors
  return colorArray
    .filter(item => !item.isEmpty)
    .map(item => ({
      hex: item.hex,
      originalIndex: item.originalIndex
    }));
}

/**
 * Converts a palette object from backend format to an array suitable for PalettePicker.
 * Supports different modes for handling transparent colors.
 * Each color item in the result includes the hex value and its original index from the palette.
 *
 * @param {Object} paletteObject - Palette object where keys are indices and values are [R, G, B, A] arrays.
 * @param {"default"|"stripTransparent"|"filterTransparent"} mode - Processing mode:
 *   - "default": Returns all colors including transparent ones (default)
 *   - "stripTransparent": Removes leading and trailing transparent colors (like trim())
 *   - "filterTransparent": Removes all transparent colors
 * @returns {Array<{hex: string, originalIndex: number}>} Array of color objects with hex values and original indices.
 *
 * @example
 * const palette = {
 *   0: [0, 0, 0, 0],
 *   1: [255, 0, 0, 255],
 *   2: [0, 255, 0, 255]
 * };
 * const result = paletteToHexArray(palette, "default");
 * // Returns: [{hex: "#00000000", originalIndex: 0}, {hex: "#FF0000", originalIndex: 1}, {hex: "#00FF00", originalIndex: 2}]
 *
 * const resultStripped = paletteToHexArray(palette, "stripTransparent");
 * // Returns: [{hex: "#FF0000", originalIndex: 1}, {hex: "#00FF00", originalIndex: 2}]
 *
 * const resultFiltered = paletteToHexArray(palette, "filterTransparent");
 * // Returns: [{hex: "#FF0000", originalIndex: 1}, {hex: "#00FF00", originalIndex: 2}]
 */
export function paletteToHexArray(paletteObject, mode = "default") {
  if (!paletteObject || typeof paletteObject !== "object") {
    return [];
  }

  const result = [];

  // Convert all colors to hex format
  for (const [index, rgba] of Object.entries(paletteObject)) {
    result.push({
      hex: getHexStringFromRgbaArray(rgba),
      originalIndex: parseInt(index, 10),
      isEmpty: isColorEmpty(rgba)
    });
  }

  switch (mode) {
    case "stripTransparent":
      return _paletteToHexArrayStripTransparent(result);
    case "filterTransparent":
      return _paletteToHexArrayFilterTransparent(result);
    case "default":
    default:
      // Return all colors including transparent ones
      return result.map(item => ({
        hex: item.hex,
        originalIndex: item.originalIndex
      }));
  }
}

/**
 * Converts an array of color objects (from paletteToHexArray) to a simple array of hex strings
 * suitable for the PalettePicker component.
 *
 * @param {Array<{hex: string, originalIndex: number}>} colorArray - Array from paletteToHexArray.
 * @returns {string[]} Array of hex color strings.
 *
 * @example
 * const colorArray = [{hex: "#FF0000", originalIndex: 1}, {hex: "#00FF00", originalIndex: 2}];
 * const hexArray = colorArrayToHexStrings(colorArray);
 * // Returns: ["#FF0000", "#00FF00"]
 */
export function colorArrayToHexStrings(colorArray) {
  if (!Array.isArray(colorArray)) {
    return [];
  }
  return colorArray.map(item => item.hex);
}

/**
 * Gets the original palette index from a display index in the filtered color array.
 *
 * @param {Array<{hex: string, originalIndex: number}>} colorArray - Array from paletteToHexArray.
 * @param {number} displayIndex - Index in the filtered/display array.
 * @returns {number|null} The original palette index, or null if not found.
 *
 * @example
 * const colorArray = [{hex: "#FF0000", originalIndex: 5}, {hex: "#00FF00", originalIndex: 10}];
 * const originalIndex = getOriginalPaletteIndex(colorArray, 1); // Returns: 10
 */
export function getOriginalPaletteIndex(colorArray, displayIndex) {
  if (
    !Array.isArray(colorArray) ||
    displayIndex < 0 ||
    displayIndex >= colorArray.length
  ) {
    return null;
  }
  return colorArray[displayIndex].originalIndex;
}

/**
 * Converts an RGBA array [R, G, B, A] to a hex color string.
 * @param {number[]} rgba - Array with [R, G, B] values (0-255) and A value (0-1).
 * @returns {string} Hex color string (e.g., "#FF0000FF").
 *
 * @example
 * const rgbaArray = [255, 0, 0, 1.0];
 * const hexString = getHexStringFromRgbaArray(rgbaArray);
 * // Returns: "#FF0000FF"
 */
export function getHexStringFromRgbaArray(rgbaArray) {
  if (!Array.isArray(rgbaArray) || rgbaArray.length < 3) {
    return "#000000";
  }
  // RGB values are 0-255, just clamp and pad
  const hex =
    "#" +
    [0, 1, 2]
      .map(i =>
        Math.max(0, Math.min(255, Math.round(rgbaArray[i])))
          .toString(16)
          .padStart(2, "0")
      )
      .join("")
      .toUpperCase();

  // If alpha provided and not fully opaque, include it
  // Alpha is in 0-1 range, so multiply by 255
  if (
    typeof rgbaArray[3] === "number" &&
    rgbaArray[3] >= 0 &&
    rgbaArray[3] <= 1 &&
    rgbaArray[3] !== 1
  ) {
    return (
      hex +
      Math.max(0, Math.min(255, Math.round(rgbaArray[3] * 255)))
        .toString(16)
        .padStart(2, "0")
        .toUpperCase()
    );
  }
  return `${hex}FF`;
}

/**
 * Converts an RGBA object {r: number, g: number, b: number, a: number} to a hex color string.
 * @param {Object} rgbaObject - Object with r, g, b values (0-255) and optional a value (0-1).
 * @returns {string} Hex color string (e.g., "#FF0000FF").
 *
 * @example
 * const rgbaObject = {r: 255, g: 0, b: 0, a: 1};
 * const hexString = getHexStringFromRgbaObject(rgbaObject);
 * // Returns: "#FF0000FF"
 */
export function getHexStringFromRgbaObject(rgbaObject) {
  // RGB values are 0-255, alpha is 0-1 (as per react-color spec)
  return getHexStringFromRgbaArray([
    rgbaObject?.r ?? 0,
    rgbaObject?.g ?? 0,
    rgbaObject?.b ?? 0,
    rgbaObject?.a ?? 1
  ]);
}

/**
 * Converts a hex color string to an RGBA array [R, G, B, A].
 * @param {string} hexString - Hex color string (e.g., "#FF0000" or "#FF0000FF").
 * @returns {number[]} RGBA array [R, G, B, A] (0-255).
 *
 * @example
 * const hexString = "#FF0000FF";
 * const rgbaArray = getRgbaArrayFromHexString(hexString);
 * // Returns: [255, 0, 0, 255]
 */
export function getRgbaArrayFromHexString(hexString) {
  if (typeof hexString !== "string") {
    return [0, 0, 0, 255];
  }

  // Remove '#' if present
  const hex = hexString.replace(/^#/, "");

  // Support 6-digit (RRGGBB) and 8-digit (RRGGBBAA)
  if (hex.length === 6) {
    // No alpha channel, use fully opaque
    const r = Number.parseInt(hex.slice(0, 2), 16);
    const g = Number.parseInt(hex.slice(2, 4), 16);
    const b = Number.parseInt(hex.slice(4, 6), 16);
    return [r, g, b, 255];
  } else if (hex.length === 8) {
    // Contains alpha
    const r = Number.parseInt(hex.slice(0, 2), 16);
    const g = Number.parseInt(hex.slice(2, 4), 16);
    const b = Number.parseInt(hex.slice(4, 6), 16);
    const a = Number.parseInt(hex.slice(6, 8), 16);
    return [r, g, b, a];
  } else {
    // Invalid input, fallback
    return [0, 0, 0, 255];
  }
}
